import { getSubdomainFranchise } from '../utils/get-subdomain';
import { httpInterceptor } from './http.interceptor';
import { CheckCpfResponse } from '../types';
import { EstatisticasCliente } from '../types/estatisticas-cliente';

class ClientService {
  private subdomain: string;

  constructor() {
    this.subdomain = getSubdomainFranchise();
  }

  /**
   * Verifica se existe um cliente com o CPF informado
   * @param cpf CPF do cliente para verificação
   * @returns Resultado da verificação do CPF
   */
  async checkCpf(cpf: string): Promise<CheckCpfResponse> {
    try {
      const cleanCpf = cpf.replace(/\D/g, '');
      const response = await httpInterceptor.get(`/client/check/${cleanCpf}`);

      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (erro) {
      console.error('Erro ao verificar CPF:', erro);
      throw new Error(erro instanceof Error ? erro.message : 'Erro ao verificar CPF');
    }
  }

  /**
   * Cadastra um novo cliente
   * @param clientData Dados do cliente para cadastro
   * @param indicadorCodigo Código do usuário indicador (opcional)
   * @returns Resultado do cadastro
   */
  async createClient(clientData: any, indicadorCodigo?: string): Promise<any> {
    try {
      const payload = {
        name: clientData.nome_razao_social,
        email: clientData.email,
        cpf: clientData.cpf_cnpj.replace(/\D/g, ''),
        phone: clientData.telefone,
        password: clientData.password,
        city: clientData.cidade,
        estate: clientData.estado,
        indicador_codigo: indicadorCodigo || null
      };

      const response = await httpInterceptor.post('/client/register', payload);

      const data = await response.json();
      
      if (!response.ok) {
        return { error: data };
      }

      return data;
    } catch (erro) {
      console.error('Erro ao cadastrar cliente:', erro);
      throw new Error(erro instanceof Error ? erro.message : 'Erro ao cadastrar cliente');
    }
  }

  /**
   * Busca um cliente pelo CPF e verifica indicação
   * @param params Parâmetros da busca (cpf e id_revendedora)
   * @returns Dados do cliente e indicação
   */
  async getUserClient(params: { cpf: string; id_revendedora: number }): Promise<any> {
    try {
      const response = await httpInterceptor.post('/client/get-user-client', {
        cpf: params.cpf,
        id_revendedora: params.id_revendedora
      });

      const data = await response.json();
      
      if (!response.ok) {
        return { success: false, error: data };
      }

      return data;
    } catch (erro) {
      console.error('Erro ao buscar cliente:', erro);
      throw new Error(erro instanceof Error ? erro.message : 'Erro ao buscar cliente');
    }
  }

  /**
   * Busca informações de CEP
   * @param cep CEP para consulta
   * @returns Dados do endereço
   */
  async searchCep(cep: string): Promise<any> {
    try {
      const cleanCep = cep.replace(/\D/g, '');
      const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
      
      if (!response.ok) {
        throw new Error('Erro ao buscar CEP');
      }

      const data = await response.json();
      return data;
    } catch (erro) {
      console.error('Erro ao buscar CEP:', erro);
      return { error: erro };
    }
  }

  /**
   * Busca todas as vendas de um cliente específico
   * @param clienteId ID do cliente
   * @returns Lista de vendas do cliente
   */
  async getVendasCliente(clienteId: number): Promise<any[]> {
    try {
      const response = await httpInterceptor.get(`/client/vendas/${clienteId}`);

      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (erro) {
      console.error('Erro ao buscar vendas do cliente:', erro);
      throw new Error(erro instanceof Error ? erro.message : 'Erro ao buscar vendas do cliente');
    }
  }

  /**
   * Busca as estatísticas resumidas de um cliente específico
   * @param clienteId ID do cliente
   * @returns Estatísticas do cliente (diamantes, vendas, indicações, revendedora)
   */
  async getEstatisticasCliente(clienteId: number): Promise<EstatisticasCliente> {
    try {
      const response = await httpInterceptor.get(`/client/estatisticas/${clienteId}`);

      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (erro) {
      console.error('Erro ao buscar estatísticas do cliente:', erro);
      throw new Error(erro instanceof Error ? erro.message : 'Erro ao buscar estatísticas do cliente');
    }
  }

  /**
   * Busca um cliente por CPF ou telefone
   * @param dado Tipo de dado para busca ('cpf_cliente' ou 'telefone_cliente')
   * @param valor Valor a ser buscado
   * @returns Dados do cliente encontrado
   */
  async getClient(dado: string, valor: string): Promise<any> {
    try {
      const response = await httpInterceptor.post('/client/get-client', {
        dado: dado,
        valor: valor,
      });
      // Se for 404 (não encontrado), retorna sem id para indicar que não existe
      if (response.status === 404) {
        return { id: null };
      }
      
      // Se for outro erro HTTP, lança erro
      if (!response.ok) {
        throw new Error(`Erro ao buscar cliente: ${response.status}`);
      }
      const data = await response.json();

      return data;
    } catch (erro) {
      console.error('Erro ao buscar cliente:', erro);
      throw new Error(erro instanceof Error ? erro.message : 'Erro ao buscar cliente');
    }
  }

  async getClientsByRevendedora(revendedoraId: number): Promise<Array<{ id: number; nome: string; cpf: string; telefone: string | null }>> {
    try {
      const response = await httpInterceptor.get(`/client/by-revendedora/${revendedoraId}`);

      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status}`);
      }

      const data = await response.json();
      return Array.isArray(data) ? data : [];
    } catch (erro) {
      console.error('Erro ao buscar clientes da revendedora:', erro);
      return [];
    }
  }
}

export const clientService = new ClientService();
